<template>
    <form @submit.prevent="save" class="clearfix pb-4">
        <top-nav-bar :goto="backPath"></top-nav-bar>

        <template v-if="step === 'information'">

            <form-group :label="trans('reports::reports.occupational_accident.date')" name="date" :required="true">
                <datepicker placeholder="Select date" id="date" name="date" v-model="report.date" />
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.time')" name="time" :required="true">
                <div class="flex">
                    <div class="select flex-1">
                        <select class="input" name="time[0]" v-model="time[0]">
                            <option value=""> – </option>
                            <option v-for="hour in hours" :value="hour">{{ parseInt(hour) }}</option>
                        </select>
                    </div>
                    <div class="input-group-addon">:</div>
                    <div class="select flex-1">
                        <select class="input text-left" name="time[1]" v-model="time[1]">
                            <option value=""> – </option>
                            <option v-for="minute in minutes" :value="minute">{{ minute }}</option>
                        </select>
                    </div>
                </div>
                <div class="flex" v-if="hasError('time.0') || hasError('time.1')">
                    <div class="flex-1">
                        <div class="help-text text-red"
                             v-if="hasError('time.0')" v-html="getError('time.0')"></div>
                    </div>
                    <div class="flex-1">
                        <div class="help-text text-red"
                             v-if="hasError('time.1')" v-html="getError('time.1')"></div>
                    </div>
                </div>
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.place')"
                        name="place" :required="true">
                <select v-model="report.place"
                        id="place"
                        name="place"
                        class="input">
                    <option v-for="item in accidentPlaces" :value="item">
                        {{ item }}
                    </option>
                </select>
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.location')"
                        name="location" :required="true">
                <input type="text" name="location" class="input" v-model="report.location" />
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.description')"
                        name="description" :required="true">
                <textarea name="description" class="input"
                          id="description" rows="10"
                          v-model="report.description">
                </textarea>
            </form-group>

        </template>

        <template v-if="step === 'details'">

            <h4 class="mb-8">{{ trans('reports::reports.occupational_accident.tabs.details') }}</h4>

            <form-group :label="trans('reports::reports.occupational_accident.what_happened_before')"
                        name="what_happened_before" :required="true">
                <textarea name="what_happened_before" class="input"
                          id="what_happened_before" rows="10"
                          v-model="report.what_happened_before">
                </textarea>
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.reason')" name="reason" :required="true">
                <textarea name="reason" class="input"
                          id="reason" rows="10"
                          v-model="report.reason">
                </textarea>
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.injury')" name="injury" :required="true">
                <select v-model="report.injury"
                        id="injury"
                        name="injury"
                        class="input">
                    <option v-for="injury in injuries" :value="injury">
                        {{ injury }}
                    </option>
                </select>
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.what_body_part_affected')"
                        name="what_body_part_affected" :required="true">
                <input type="text" name="what_body_part_affected" class="input"
                       v-model="report.what_body_part_affected" />
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.photo_documentation')"
                        name="photo_documentation">
                <upload-file-button input-name="photo_documentation"
                                    @change="(event) => processFile(event, 'photo_documentation')"
                />
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.is_expected_sick_leave')"
                        name="is_expected_sick_leave" :required="true">
                <select v-model="report.is_expected_sick_leave" id="is_expected_sick_leave"
                        name="is_expected_sick_leave" class="input">
                    <option v-for="yes_no_option in yes_no_options" :value="yes_no_option.value">
                        {{yes_no_option.label}}
                    </option>
                </select>
            </form-group>

            <form-group v-if="report.is_expected_sick_leave === 1"
                        :label="trans('reports::reports.occupational_accident.sick_leave_from')"
                        name="sick_leave_from" :required="true">
                <datepicker placeholder="Select date" id="sick_leave_from"
                            name="sick_leave_from" v-model="report.sick_leave_from" />
            </form-group>

            <form-group v-if="report.is_expected_sick_leave === 1"
                        :label="trans('reports::reports.occupational_accident.sick_leave_to')"
                        label="Sick Leave To" name="sick_leave_to" :required="true">
                <datepicker placeholder="Select date" id="sick_leave_to"
                            name="sick_leave_to" v-model="report.sick_leave_to" />
            </form-group>

            <form-group :label="trans('reports::reports.occupational_accident.visited_own_doctor')"
                        name="visited_own_doctor"
                        :required="true">
                <select v-model="report.visited_own_doctor" id="visited_own_doctor"
                        name="visited_own_doctor" class="input">
                    <option v-for="yes_no_option in yes_no_options" :value="yes_no_option.value">
                        {{yes_no_option.label}}
                    </option>
                </select>
            </form-group>

            <form-group v-if="report.visited_own_doctor === 1"
                        :label="trans('reports::reports.occupational_accident.doctors_note')"
                        name="doctors_note">
                <upload-file-button input-name="doctors_note" :file-name="report.doctors_note.path || null"
                                    @change="(event) => processFile(event, 'doctors_note')"/>
            </form-group>
        </template>

        <div class="block md:inline-flex md:float-right">
            <button class="btn btn--colored__blue w-full" type="submit" v-html="buttonText"></button>
        </div>
    </form>
</template>

<script>
    import InteractsWithDates from '@/mixins/InteractsWithDates';
    import NeedsUsers from '@/mixins/NeedsUsers';
    import HasSteps from '@/mixins/HasSteps';
    import IsReport from '@/mixins/IsReport';
    import SetsReportData from '@/mixins/SetsReportData';
    import UploadFileButton from "../../components/UploadFileButton";

    export default {
        components: {UploadFileButton},

        mixins: [InteractsWithDates, NeedsUsers, HasSteps, IsReport, SetsReportData],

        data() {
            return {
                type: 'occupational-accident',
                steps: [
                    {name: this.trans('reports::reports.occupational_accident.tabs.information'), id: 'information'},
                    {name: this.trans('reports::reports.occupational_accident.tabs.details'), id: 'details'},
                ],
                yes_no_options: [
                    {value: '', label: '–'},
                    {value: 1, label: 'Yes'},
                    {value: 0, label: 'No'},
                ],
                time: ['', ''],
                report: {
                    date: '',
                    time: '',
                    place: '',
                    location: '',
                    description: '',
                    what_happened_before: '',
                    reason: '',
                    injury: '',
                    what_body_part_affected: '',
                    photo_documentation: '',
                    is_expected_sick_leave: '',
                    sick_leave_from: '',
                    sick_leave_to: '',
                    visited_own_doctor: '',
                    doctors_note: '',
                },
                hours: _.range(0, 24),
                minutes: _.range(0, 60, 5)
            }
        },

        created() {
            if (this.$parent.reportData) {
                this.setData(this.$parent.reportData, 'date')
                this.setData(this.$parent.reportData, 'time', 'std')
                this.setData(this.$parent.reportData.content, ['place', 'location', 'description']);
                this.setData(this.$parent.reportData.content, [
                    'what_happened_before', 'reason', 'injury',
                    'what_body_part_affected', 'photo_documentation',
                    'is_expected_sick_leave', 'sick_leave_from', 'sick_leave_to',
                    'visited_own_doctor', 'doctors_note',
                ], 'details');

                if (this.report.time) {
                    this.time = this.report.time.split(':');
                }
            }
        },

        computed: {
            injuries() {
                return this.$parent.injuries;
            },
            accidentPlaces() {
                return this.$parent.accidentPlaces;
            },
        },

        methods: {
            processFile(event, key) {
                let files = event.target.files;

                if (files.length) {
                    this.report[key] = files[0];
                }
            },
            hasError(name) {
                return this.$store.getters.hasError(name)
            },
            getError(name) {
                return this.$store.getters.getError(name)
            }
        },
    }
</script>
